import { get, postJson, patch, API_BASE_URL } from './apiClient';

export type QuotationStatus = 'needs_review' | 'finalized' | 'sent' | 'rejected';

export interface PendingAmendmentDto {
  addedAt: string;
  sourceEmailId?: string | null;
  addedItems: Array<{ itemName: string; quantity: number; uom?: string | null }>;
  reviewed: boolean;
}

export interface QuotationListItemDto {
  _id: string;
  quotationNumber: string;
  revisionNumber?: number;
  revisionOf?: string | null;
  rfqId: string | { _id: string; rfqId?: string; subject?: string };
  rfqNumber?: string;
  /** Email subject from linked RFQ (set by GET /api/quotations list) */
  rfqSubject?: string | null;
  customer: {
    customerName?: string;
    vesselName?: string;
    etaOfVessel?: string;
  };
  status: QuotationStatus;
  /** Set when a customer email requested item addition(s) auto-applied by the
   * quotation-amendment watcher; drives a "review before sending" banner. */
  pendingAmendment?: PendingAmendmentDto | null;
  summary: {
    totalItems: number;
    itemsWithPricing: number;
    itemsWithoutPricing: number;
    totalValue: number;
    currency: string;
    fxRate?: number;
  };
  createdAt: string;
  generatedAt: string;
  /** Wall-clock timing of the generation pass itself — null on any quotation generated
   * before this was added, so the UI must treat that as "unknown", not zero. */
  generationStartedAt?: string | null;
  generationCompletedAt?: string | null;
  generationDurationMs?: number | null;
}

export interface VendorOptionDto {
  vendorName: string;
  unitPrice: number;
  totalPrice: number;
  currency: string;
  pricingSource: 'historical_quotation' | 'yelo_api' | 'price_list' | 'client_provided';
  pricingConfidence: number;
  pricingDetails?: any;
  internalRemarks?: string;
}

export interface QuotationItemDto {
  lineNumber: number;
  rfqItemId?: string | null;
  itemName: string;
  itemDescription?: string;
  impaCode?: string;
  impaDescription?: string;
  fromInventory?: boolean;
  quantity: number;
  uom?: string;
  unitPrice?: number;
  currency: string;
  totalPrice?: number;
  markup?: number; // Markup percentage for this item
  unitSellingPrice?: number; // Unit selling price (can be manually overridden)
  totalSellingPrice?: number; // Total selling price (can be manually overridden)
  sellingPriceLocked?: boolean; // True when the user hand-typed a selling price; cost-stack recomputes skip it
  competitorPrice?: number; // Competitor's price for comparison
  gst?: boolean; // GST flag for 18% GST calculation
  pricingSource: 'historical_quotation' | 'yelo_api' | 'price_list' | 'vendor_invoice' | 'client_provided' | 'not_found';
  pricingConfidence: number;
  selectedVendor?: string;
  vendorOptions?: VendorOptionDto[];
  notes?: string;
  internalRemarks?: string;
  remarks?: string;
  moreDetails?: string;
  /** Basis / unit conversion info from pricing engine (atomic count, weight conversion, etc.) */
  pricingDetails?: Record<string, unknown>;
}

export interface CustomExpenseDto {
  _id?: string;
  label: string;
  amount: number;
  currency?: string;
  notes?: string;
}

/** Snapshot of the matched customer's commercial terms at generation time (see
 * src/services/quotationGeneration.service.js). commission/softwareFees/vat are
 * percentages; managementFees/portFees/otherCharges are flat amounts — all load
 * into cost before markup (client/src/lib/pricing.ts computes the loading factor). */
export interface QuotationCustomerTermsDto {
  region?: string;
  salesPerson?: string;
  purchaser?: string;
  creditTerms?: string;
  liabilityLimit?: number | null;
  fixedMarkup?: number | null;
  commission?: number | null;
  vat?: number | null;
  managementFees?: number | null;
  softwareFees?: number | null;
  portFees?: number | null;
  otherCharges?: number | null;
}

export interface QuotationDetailDto extends QuotationListItemDto {
  items: QuotationItemDto[];
  /** Set once a Client record has been matched/assigned to this quotation. Populated with
   * the client's name by the quotation-detail endpoint; other endpoints may return it as a
   * plain id string. */
  customerId?: string | { _id: string; name: string } | null;
  customerTerms?: QuotationCustomerTermsDto;
  /** VAT percent absorbed into the cost-stack loading factor at generation time
   * (non-India clients only); internal-only, not a customer-facing line. */
  costStackVat?: { percent?: number | null; source?: 'client' | 'country_default' | null };
  terms?: {
    paymentTerms?: string;
    deliveryTerms?: string;
    warranty?: string;
    additionalNotes?: string;
  };
  /** Numbered T&C clause block rendered on the PDF/Excel exports — auto-attached at
   * generation time from a per-country QuotationTermsTemplate, editable per-quotation. */
  termsAndConditions?: string[];
  discountPercentage?: number;
  transportationCharges?: number;
  additionalCharges?: number;
  customExpenses?: CustomExpenseDto[];
  competitorDiscountPercentage?: number;
  competitorTransportationCharges?: number;
  competitorAdditionalCharges?: number;
  competitorCustomExpenses?: CustomExpenseDto[];
  validUntil?: string;
  reviewedAt?: string;
  reviewedBy?: string;
  reviewNotes?: string;
  /** Prefill defaults for the Send screen so the quotation replies INTO the original
   * inquiry conversation: recipient (the address the RFQ came from), a "Re: <original
   * subject>" subject (Outlook groups on the normalised subject), and whether a thread
   * exists to reply under. Computed server-side in GET /quotations/:id. The actual
   * In-Reply-To/References headers are set on send regardless of edits here. */
  replyDefaults?: {
    to: string[];
    subject: string;
    threaded: boolean;
  };
}

export interface QuotationListResponse {
  success: boolean;
  data: {
    quotations: QuotationListItemDto[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  };
}

export interface GenerateQuotationResponse {
  success: boolean;
  message: string;
  data?: any;
}

// Fetch all quotations with filters and pagination
export async function fetchQuotationList(params: {
  page?: number;
  limit?: number;
  /** Single status or comma-separated list of statuses. */
  status?: QuotationStatus | string;
  customer?: string;
  rfqNumber?: string;
  quotationNumber?: string;
  /** Free-text search across email subject, client name/email, RFQ & quote number. */
  search?: string;
  /** Inclusive createdAt range (YYYY-MM-DD). */
  dateFrom?: string;
  dateTo?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
} = {}) {
  const searchParams = new URLSearchParams();

  if (params.page) searchParams.set('page', String(params.page));
  if (params.limit) searchParams.set('limit', String(params.limit));
  if (params.status) searchParams.set('status', params.status);
  if (params.customer) searchParams.set('customer', params.customer);
  if (params.rfqNumber) searchParams.set('rfqNumber', params.rfqNumber);
  if (params.quotationNumber) searchParams.set('quotationNumber', params.quotationNumber);
  if (params.search) searchParams.set('search', params.search);
  if (params.dateFrom) searchParams.set('dateFrom', params.dateFrom);
  if (params.dateTo) searchParams.set('dateTo', params.dateTo);
  if (params.sortBy) searchParams.set('sortBy', params.sortBy);
  if (params.sortOrder) searchParams.set('sortOrder', params.sortOrder);

  return get<QuotationListResponse>(`/quotations?${searchParams.toString()}`);
}

// Fetch quotation by ID
export async function fetchQuotationDetail(id: string) {
  const response = await get<{ success: boolean; data: QuotationDetailDto }>(`/quotations/${id}`);
  return response.data;
}

// Generate quotations for all processed RFQs
export async function generateQuotations() {
  return postJson<GenerateQuotationResponse>('/quotations/generate', {});
}

// Generate quotation for specific RFQ
export async function generateQuotationForRFQ(rfqId: string) {
  return postJson<GenerateQuotationResponse>(`/quotations/generate/${rfqId}`, {});
}

// Update quotation status
export async function updateQuotationStatus(id: string, status: QuotationStatus, reviewNotes?: string) {
  return postJson<{ success: boolean; data: QuotationDetailDto }>(`/quotations/${id}/status`, {
    status,
    reviewNotes
  });
}

// Update quotation status (simple PATCH)
export async function updateStatus(id: string, status: QuotationStatus) {
  console.log('[API] Calling PATCH /quotations/' + id + ' with status:', status);
  const response = await patch<{ success: boolean; data: QuotationDetailDto }>(`/quotations/${id}`, {
    status
  });
  console.log('[API] PATCH response:', response);
  return response;
}

// Update quotation items and details
export async function updateQuotation(id: string, updates: Partial<QuotationDetailDto>) {
  return patch<{ success: boolean; data: QuotationDetailDto }>(`/quotations/${id}`, updates);
}

/**
 * Delete several line items in ONE request, by their stable lineNumber. One
 * server-side write instead of N concurrent PATCHes, so rapid multi-delete can't
 * race the quotation's version and fail. Returns the updated quotation.
 */
export async function bulkDeleteQuotationItems(id: string, lineNumbers: number[]) {
  return postJson<{ success: boolean; data: QuotationDetailDto; removed: number }>(
    `/quotations/${id}/items/bulk-delete`,
    { lineNumbers },
  );
}

/** Create a revision (<base>-N) of a quotation — customer asked for added lines / qty / discount. */
export async function reviseQuotation(id: string) {
  return postJson<{ success: boolean; data: QuotationDetailDto }>(`/quotations/${id}/revise`, {});
}

/**
 * Manually (re)assign the Client behind a quotation — for RFQs where
 * auto-matching (by sender email, then company name) couldn't find one.
 * Re-applies the client's currency/discount/VAT/cost-stack markup.
 */
export async function assignQuotationClient(id: string, clientId: string) {
  return postJson<{ success: boolean; data: QuotationDetailDto; message: string }>(
    `/quotations/${id}/assign-client`,
    { clientId }
  );
}

/**
 * Restore a quotation's Terms & Conditions to the standard per-country template —
 * recovery path after the old single-textarea editor wiped clauses, or to reset
 * back to standard. Replaces the whole termsAndConditions array.
 */
export async function resetQuotationTermsToDefault(id: string) {
  return postJson<{ success: boolean; data: QuotationDetailDto; message: string }>(
    `/quotations/${id}/terms/reset-to-default`,
    {}
  );
}

export interface PriceLookupResult {
  itemName: string;
  quantity: number;
  uom: string;
  unitPrice: number | null;
  totalPrice: number | null;
  currency: string;
  pricingSource: QuotationItemDto['pricingSource'];
  pricingConfidence: number;
  pricingDetails?: Record<string, unknown>;
  selectedVendor?: string | null;
  vendorOptions: VendorOptionDto[];
  internalRemarks?: string | null;
}

// Look up a price for an ad-hoc item using the same pipeline used when generating quotations
// (historical quotations → Yelo API → price lists → vendor invoices).
export async function lookupItemPrice(payload: {
  itemName: string;
  quantity?: number;
  uom?: string;
}) {
  const res = await postJson<{ success: boolean; data: PriceLookupResult }>(
    `/quotations/price-lookup`,
    payload
  );
  return res.data;
}

// Download quotation as PDF
export function downloadQuotationPDF(id: string, discountPercentage: number = 0) {
  const token = localStorage.getItem('token');

  const url = `${API_BASE_URL}/quotations/${id}/download/pdf?discount=${discountPercentage}`;

  // Create a temporary link and trigger download
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', '');

  // Add authorization header by opening in new window with fetch
  fetch(url, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`
    }
  })
    .then(response => response.blob())
    .then(blob => {
      const blobUrl = window.URL.createObjectURL(blob);
      link.href = blobUrl;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    })
    .catch(error => {
      console.error('Error downloading PDF:', error);
      throw error;
    });
}

// Download quotation as Excel
export function downloadQuotationExcel(id: string, discountPercentage: number = 0, templateId?: string) {
  const token = localStorage.getItem('token');

  const params = new URLSearchParams({
    discount: discountPercentage.toString()
  });

  if (templateId) {
    params.append('template', templateId);
  }

  const url = `${API_BASE_URL}/quotations/${id}/download/excel?${params.toString()}`;

  // Create a temporary link and trigger download
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', '');

  // Add authorization header by opening in new window with fetch
  fetch(url, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`
    }
  })
    .then(response => response.blob())
    .then(blob => {
      const blobUrl = window.URL.createObjectURL(blob);
      link.href = blobUrl;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    })
    .catch(error => {
      console.error('Error downloading Excel:', error);
      throw error;
    });
}

// Download quotation as PDF (Client View - Limited Columns)
export function downloadQuotationClientPDF(id: string, discountPercentage: number = 0) {
  const token = localStorage.getItem('token');

  const url = `${API_BASE_URL}/quotations/${id}/download/client-pdf?discount=${discountPercentage}`;

  // Create a temporary link and trigger download
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', '');

  // Add authorization header by opening in new window with fetch
  fetch(url, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`
    }
  })
    .then(response => response.blob())
    .then(blob => {
      const blobUrl = window.URL.createObjectURL(blob);
      link.href = blobUrl;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    })
    .catch(error => {
      console.error('Error downloading Client PDF:', error);
      throw error;
    });
}

// Download quotation as Excel (Client View - Limited Columns)
export function downloadQuotationClientExcel(id: string, discountPercentage: number = 0, templateId?: string) {
  const token = localStorage.getItem('token');

  const params = new URLSearchParams({
    discount: discountPercentage.toString()
  });

  if (templateId) {
    params.append('template', templateId);
  }

  const url = `${API_BASE_URL}/quotations/${id}/download/client-excel?${params.toString()}`;

  // Create a temporary link and trigger download
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', '');

  // Add authorization header by opening in new window with fetch
  fetch(url, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`
    }
  })
    .then(response => response.blob())
    .then(blob => {
      const blobUrl = window.URL.createObjectURL(blob);
      link.href = blobUrl;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    })
    .catch(error => {
      console.error('Error downloading Client Excel:', error);
      throw error;
    });
}

// Update selected vendor for a quotation item
export const updateQuotationItemVendor = async (
  quotationId: string,
  itemIndex: number,
  vendorName: string,
  /** Id of the vendor price row backing this price, or `null` to clear a previous link.
   * Pass `undefined` to leave whatever is stored untouched. */
  vendorPriceItemId?: string | null
): Promise<{ success: boolean; data: QuotationDetailDto }> => {
  const token = localStorage.getItem('token');

  const response = await fetch(`${API_BASE_URL}/quotations/${quotationId}/items/${itemIndex}/vendor`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify(
      vendorPriceItemId === undefined
        ? { vendorName }
        : { vendorName, vendorPriceItemId }
    ),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to update vendor');
  }

  return response.json();
};

/** A product data sheet (PDS/TDS) belonging to one or more lines on a quotation. */
export interface QuotationDataSheet {
  id: string;
  filename: string;
  mimeType: string;
  size: number;
  /** The product the sheet describes. */
  productName: string;
  /** 'matched' (automatic) | 'manual' (linked by a person) */
  matchStatus: string;
  matchConfidence: number;
  matchedBy: string;
  /** The quotation lines this sheet covers. Empty for a vendor file not tied to a line. */
  itemNames: string[];
  /** True when this sheet is bound to a quoted line and should be ticked by default.
   * False for the vendor's other price list attachments — offered, but opt-in. */
  autoAttach?: boolean;
}

// List the data sheets that will be sent with this quotation
export const fetchQuotationDataSheets = async (
  quotationId: string
): Promise<QuotationDataSheet[]> => {
  const token = localStorage.getItem('token');

  const response = await fetch(`${API_BASE_URL}/quotations/${quotationId}/data-sheets`, {
    headers: { 'Authorization': `Bearer ${token}` },
  });

  if (!response.ok) {
    console.error('Failed to load quotation data sheets:', response.status);
    return [];
  }

  const json = await response.json().catch(() => ({}));
  return Array.isArray(json?.data) ? json.data : [];
};

// Send quotation via email
export const sendQuotation = async (
  quotationId: string,
  emailData: {
    rfqNumber: string;
    date: string;
    clientName: string;
    vesselName: string;
    subject: string;
    description: string;
    recipients?: string[];
    /** Data sheets to send with the quotation. An explicit array (empty included) is
     *  honoured exactly; OMITTING the field attaches the sheets tied to a quoted line
     *  (a chemicals quotation is expected to carry those) but NOT the vendor's other
     *  unmatched attachments, which always stay opt-in. */
    attachDataSheetIds?: string[];
  },
  templateId?: string,
  /** Attachment format: 'excel' (default, template-driven) or 'pdf' (one fixed format). */
  format?: 'excel' | 'pdf'
): Promise<{
  success: boolean;
  data: QuotationDetailDto;
  message?: string;
  dataSheetsSent?: string[];
  dataSheetErrors?: Array<{ id: string; filename: string; message: string }>;
}> => {
  const token = localStorage.getItem('token');

  const response = await fetch(`${API_BASE_URL}/quotations/${quotationId}/send`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      ...emailData,
      templateId: templateId || 'default',
      format: format || 'excel'
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to send quotation');
  }

  return response.json();
};
